import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Button, Table, Tag, Spin, Typography } from 'antd'
import moment from 'moment'
import numeral from 'numeral'

import API from '../../../api/PriceApi'
import * as EndPoints from '../../../api/EndPoints'

const { Text } = Typography

const PERIODS = [
  { value: 7, label: '7 วัน' },
  { value: 15, label: '15 วัน' },
  { value: 30, label: '30 วัน' },
  { value: 60, label: '60 วัน' },
  { value: 90, label: '90 วัน' },
  { value: 365, label: 'ทั้งหมด' }
]

const fmtNumber = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '-'
  return numeral(v).format('0,0.00')
}

const fmtDate = (value) => {
  if (!value) return '-'
  return moment.utc(value).utcOffset(7).format('YYYY-MM-DD HH:mm')
}

const niceStep = (range) => {
  const p = Math.pow(10, Math.floor(Math.log10(range)))
  const m = range / p
  if (m <= 1) return p * 0.2
  if (m <= 2) return p * 0.5
  if (m <= 5) return p
  return p * 2
}

// Render line chart with area fill onto a canvas. Returns a redraw closure
// that the parent can call from a tooltip handler to overlay a crosshair.
const renderChart = (canvas, data, labels, color, highlightIdx) => {
  if (!canvas) return null
  const dpr = window.devicePixelRatio || 1
  const rect = canvas.parentElement.getBoundingClientRect()
  const W = rect.width
  const H = 300
  canvas.width = W * dpr
  canvas.height = H * dpr
  canvas.style.width = W + 'px'
  canvas.style.height = H + 'px'
  const ctx = canvas.getContext('2d')
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, W, H)

  if (!data || data.length < 2) {
    ctx.fillStyle = '#8c8c8c'
    ctx.font = '13px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('ข้อมูลไม่พอสำหรับวาดกราฟ', W / 2, H / 2)
    return null
  }

  const pad = { top: 20, right: 20, bottom: 40, left: 65 }
  const cw = W - pad.left - pad.right
  const ch = H - pad.top - pad.bottom

  const mn = Math.min.apply(null, data)
  const mx = Math.max.apply(null, data)
  const rng = mx - mn || 1
  const step = niceStep(rng)
  const yMin = Math.floor(mn / step) * step
  const yMax = Math.ceil(mx / step) * step
  const yRng = yMax - yMin || 1
  const toX = (i) => pad.left + (i / (data.length - 1)) * cw
  const toY = (v) => pad.top + (1 - (v - yMin) / yRng) * ch

  ctx.strokeStyle = 'rgba(0,0,0,.06)'
  ctx.lineWidth = 1
  ctx.font = '10px monospace'
  ctx.fillStyle = '#8c8c8c'
  ctx.textAlign = 'right'
  for (let v = yMin; v <= yMax + step * 0.01; v += step) {
    const y = toY(v)
    ctx.beginPath()
    ctx.moveTo(pad.left, y)
    ctx.lineTo(W - pad.right, y)
    ctx.stroke()
    ctx.fillText(numeral(v).format('0,0.00'), pad.left - 8, y + 3.5)
  }

  ctx.textAlign = 'center'
  const maxLabels = Math.max(2, Math.floor(cw / 70))
  const labelStep = Math.max(1, Math.ceil(labels.length / maxLabels))
  for (let i = 0; i < labels.length; i += labelStep) {
    ctx.fillText(labels[i], toX(i), H - pad.bottom + 18)
  }

  const drawPath = () => {
    ctx.beginPath()
    ctx.moveTo(toX(0), toY(data[0]))
    for (let i = 1; i < data.length; i++) {
      const x0 = toX(i - 1)
      const y0 = toY(data[i - 1])
      const x1 = toX(i)
      const y1 = toY(data[i])
      const cx = (x0 + x1) / 2
      ctx.bezierCurveTo(cx, y0, cx, y1, x1, y1)
    }
  }

  const grad = ctx.createLinearGradient(0, pad.top, 0, pad.top + ch)
  grad.addColorStop(0, color + '40')
  grad.addColorStop(1, color + '00')
  drawPath()
  ctx.lineTo(toX(data.length - 1), pad.top + ch)
  ctx.lineTo(toX(0), pad.top + ch)
  ctx.closePath()
  ctx.fillStyle = grad
  ctx.fill()

  drawPath()
  ctx.strokeStyle = color
  ctx.lineWidth = 2.5
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.stroke()

  if (data.length <= 60) {
    for (let i = 0; i < data.length; i++) {
      ctx.beginPath()
      ctx.arc(toX(i), toY(data[i]), 3, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 2
      ctx.stroke()
    }
  }

  if (highlightIdx != null && highlightIdx >= 0 && highlightIdx < data.length) {
    const px = toX(highlightIdx)
    const py = toY(data[highlightIdx])
    ctx.setLineDash([4, 4])
    ctx.strokeStyle = 'rgba(0,0,0,.18)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(px, pad.top)
    ctx.lineTo(px, pad.top + ch)
    ctx.stroke()
    ctx.setLineDash([])
    ctx.beginPath()
    ctx.arc(px, py, 5, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = 2
    ctx.stroke()
  }

  return { pad, W, H, cw, ch, toX, toY }
}

const HistoryChart = ({ data, labels, color }) => {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [hoverIdx, setHoverIdx] = useState(null)
  const lastBoxRef = useRef(null)

  useEffect(() => {
    lastBoxRef.current = renderChart(
      canvasRef.current,
      data,
      labels,
      color,
      hoverIdx
    )
  }, [data, labels, color, hoverIdx])

  useEffect(() => {
    const onResize = () => {
      lastBoxRef.current = renderChart(
        canvasRef.current,
        data,
        labels,
        color,
        hoverIdx
      )
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [data, labels, color, hoverIdx])

  const handleMove = (e) => {
    const box = lastBoxRef.current
    if (!box || !data || data.length < 2) return
    const rect = canvasRef.current.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const mx = clientX - rect.left
    if (mx < box.pad.left || mx > box.W - box.pad.right) {
      if (hoverIdx !== null) setHoverIdx(null)
      if (tooltipRef.current) tooltipRef.current.style.opacity = '0'
      return
    }
    const idx = Math.round(
      ((mx - box.pad.left) / box.cw) * (data.length - 1)
    )
    const ci = Math.max(0, Math.min(data.length - 1, idx))
    if (ci !== hoverIdx) setHoverIdx(ci)
    if (tooltipRef.current) {
      const px = box.toX(ci)
      const py = box.toY(data[ci])
      tooltipRef.current.innerHTML =
        '<div style="font-size:11px;color:#8c8c8c">' +
        labels[ci] +
        '</div><div style="font-family:monospace;font-weight:600">' +
        numeral(data[ci]).format('0,0.00') +
        '</div>'
      tooltipRef.current.style.opacity = '1'
      let tx = px + 12
      let ty = py - 40
      if (tx + 140 > box.W) tx = px - 150
      if (ty < 5) ty = py + 12
      tooltipRef.current.style.left = tx + 'px'
      tooltipRef.current.style.top = ty + 'px'
    }
  }

  const handleLeave = () => {
    setHoverIdx(null)
    if (tooltipRef.current) tooltipRef.current.style.opacity = '0'
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        overflow: 'hidden'
      }}>
      <canvas
        ref={canvasRef}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        onTouchMove={handleMove}
        onTouchEnd={handleLeave}
        style={{ display: 'block', width: '100%', cursor: 'crosshair' }}
      />
      <div
        ref={tooltipRef}
        style={{
          position: 'absolute',
          pointerEvents: 'none',
          background: '#fff',
          border: '1px solid #d9d9d9',
          borderRadius: 6,
          padding: '4px 8px',
          fontSize: 12,
          opacity: 0,
          transition: 'opacity .12s',
          boxShadow: '0 2px 8px rgba(0,0,0,.08)',
          whiteSpace: 'nowrap',
          zIndex: 5
        }}
      />
    </div>
  )
}

const StatCard = ({ label, value, color }) => (
  <div
    style={{
      padding: '8px 14px',
      borderRadius: 8,
      border: '1px solid #f0f0f0',
      background: '#fafafa',
      minWidth: 110
    }}>
    <div
      style={{
        fontFamily: 'monospace',
        fontWeight: 600,
        fontSize: 16,
        color: color || undefined
      }}>
      {value}
    </div>
    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>{label}</div>
  </div>
)

const HistoryModal = ({ visible, record, onClose }) => {
  const [period, setPeriod] = useState(30)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!visible || !record || !record.id) return
    let cancelled = false
    setLoading(true)
    API.get(EndPoints.PRICE + '/' + record.id + '/history', {
      params: { days: period }
    })
      .then((res) => {
        if (cancelled) return
        const rows = Array.isArray(res.data) ? res.data : []
        // Backend returns DESC; flip to ASC for chart x-axis.
        setHistory(rows.slice().reverse())
      })
      .catch(() => {
        if (!cancelled) setHistory([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [visible, record, period])

  const chartData = useMemo(() => {
    if (!history.length) return { values: [], labels: [], color: '#1890ff' }
    const values = history.map((h) => {
      const v = h.source_price != null ? h.source_price : h.price
      return Number(v) || 0
    })
    const labels = history.map((h) => {
      const at = h.fetched_at || h.trading_day
      return at ? moment.utc(at).utcOffset(7).format('DD/MM') : ''
    })
    const first = values[0]
    const last = values[values.length - 1]
    const color = last > first ? '#52c41a' : last < first ? '#f5222d' : '#1890ff'
    return { values, labels, color }
  }, [history])

  const stats = useMemo(() => {
    const vs = chartData.values
    if (!vs.length) return null
    const latest = vs[vs.length - 1]
    const high = Math.max.apply(null, vs)
    const low = Math.min.apply(null, vs)
    const avg = vs.reduce((a, b) => a + b, 0) / vs.length
    return { latest, high, low, avg }
  }, [chartData])

  const tableColumns = [
    {
      title: 'วันที่/เวลา',
      dataIndex: 'fetched_at',
      key: 'fetched_at',
      render: fmtDate
    },
    {
      title: 'Trading Day',
      dataIndex: 'trading_day',
      key: 'trading_day',
      render: (v) => v || '-'
    },
    {
      title: 'Source',
      dataIndex: 'source_price',
      key: 'source_price',
      align: 'right',
      render: (v, r) => {
        const val = v != null ? v : r.price
        return (
          <span style={{ fontFamily: 'monospace' }}>
            {fmtNumber(val)}
            {r.source_currency && (
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                {r.source_currency}
              </Text>
            )}
          </span>
        )
      }
    },
    {
      title: 'Target',
      dataIndex: 'target_price',
      key: 'target_price',
      align: 'right',
      render: (v, r) => (
        <span style={{ fontFamily: 'monospace' }}>
          {fmtNumber(v)}
          {v != null && r.target_currency && (
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
              {r.target_currency}
            </Text>
          )}
        </span>
      )
    },
    {
      title: 'Closing',
      dataIndex: 'is_closing',
      key: 'is_closing',
      align: 'center',
      render: (v) => (v ? <Tag color="blue">CLOSE</Tag> : '')
    }
  ]

  const title = record
    ? (record.name || '') + (record.name_en ? ' — ' + record.name_en : '')
    : 'ประวัติราคา'

  // history (ASC) -> table desc for readability
  const tableData = history.slice().reverse()

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={onClose}
      width={860}
      destroyOnClose
      footer={[
        <Button key="close" onClick={onClose}>
          ปิด
        </Button>
      ]}>
      <Spin spinning={loading}>
        {stats && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <StatCard label="ล่าสุด" value={fmtNumber(stats.latest)} />
            <StatCard label="สูงสุด" value={fmtNumber(stats.high)} color="#52c41a" />
            <StatCard label="ต่ำสุด" value={fmtNumber(stats.low)} color="#f5222d" />
            <StatCard label="เฉลี่ย" value={fmtNumber(stats.avg)} />
            <StatCard label="จำนวนจุด" value={String(chartData.values.length)} />
          </div>
        )}

        <Button.Group style={{ marginBottom: 12 }}>
          {PERIODS.map((p) => (
            <Button
              key={p.value}
              size="small"
              type={p.value === period ? 'primary' : 'default'}
              onClick={() => setPeriod(p.value)}>
              {p.label}
            </Button>
          ))}
        </Button.Group>

        <HistoryChart
          data={chartData.values}
          labels={chartData.labels}
          color={chartData.color}
        />

        <Table
          style={{ marginTop: 16 }}
          size="small"
          rowKey={(r) => r.id}
          dataSource={tableData}
          columns={tableColumns}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'ไม่มีข้อมูลในช่วงเวลาที่เลือก' }}
        />
      </Spin>
    </Modal>
  )
}

export default HistoryModal
